<template>
    <Head title="Create SLA Policy" />

    <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <!-- Back Button -->
        <div class="mb-4">
            <Link
                :href="route('settings.incidents.sla-policies.index')"
                class="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to SLA Policies
            </Link>
        </div>

        <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center gap-3">
                    <div class="flex-1">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create New SLA Policy</h2>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define response and investigation time requirements for incidents matching specific criteria.</p>
                    </div>
                    <InfoButton title="Creating SLA Policies" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Create SLA Policy page allows you to establish Service Level Agreements that define
                            time-based performance commitments for incident management. Each SLA policy specifies
                            response timeframes (when triage must occur) and investigation timeframes (when full
                            investigations must complete), matched to incidents based on type, severity, or other
                            criteria. Well-designed SLA policies ensure timely incident handling, provide accountability,
                            support compliance requirements, and enable performance measurement through automated breach
                            tracking.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Policy Name:</strong> Enter a descriptive name that clearly identifies the
                              purpose (e.g., "Critical Safety - 2hr Response", "Routine Operational - 24hr")<br>
                            • <strong>Description:</strong> Explain what this policy covers and why these timeframes
                              were established<br>
                            • <strong>Incident Types:</strong> Select which incident types this policy applies to
                              (leave empty to apply to all types)<br>
                            • <strong>Severities:</strong> Choose severity levels covered (Low, Medium, High, Critical)
                              - leave empty to apply to all severities<br>
                            • <strong>Response Time Hours:</strong> Required - hours from incident report to triage
                              completion. This is when a manager must assess and assign the incident<br>
                            • <strong>Investigation Time Hours:</strong> Optional - hours from investigation start to
                              completion. Only applies if triage determines investigation is needed<br>
                            • <strong>Priority:</strong> Set evaluation order (1-100, higher = first) - used when
                              multiple policies match the same incident<br>
                            • <strong>Active Status:</strong> Check to activate immediately, or leave unchecked to
                              save as inactive<br>
                            • Click <strong>Create SLA Policy</strong> to save, or <strong>Cancel</strong> to discard
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Start with critical incidents</strong> - Define strict SLAs for high-severity
                              or safety-critical incidents first<br>
                            • <strong>Be realistic about capacity</strong> - Set timeframes your team can actually
                              meet given staffing and resources<br>
                            • <strong>Consider business hours</strong> - 8-hour response SLA with 8-5 coverage means
                              incidents after 5pm breach next business day<br>
                            • <strong>Response time is mandatory</strong> - Every incident must be triaged within
                              this timeframe<br>
                            • <strong>Investigation time is conditional</strong> - Only applies if triage decision
                              selects "Investigation" path<br>
                            • <strong>Use priority wisely</strong> - Higher numbers evaluate first - ensure critical
                              incident policies have highest priority<br>
                            • <strong>Leave criteria broad initially</strong> - Empty incident types = applies to all,
                              empty severities = applies to all<br>
                            • <strong>Create specific policies for exceptions</strong> - Use high-priority policies
                              for special cases that need different SLAs<br>
                            • <strong>Document your rationale</strong> - Use description field to explain why these
                              timeframes were chosen<br>
                            • <strong>Test before activating</strong> - Create as inactive, review, then activate when
                              ready<br>
                            • New policies apply immediately to matching incidents once activated
                        </p>
                    </InfoButton>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                <!-- Basic Information -->
                <div class="space-y-6">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>

                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300 dark:border-red-500': form.errors.name }"
                            placeholder="e.g., Critical Safety Incidents - 2 Hour Response"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                    </div>

                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            placeholder="Describe when this SLA policy applies..."
                        ></textarea>
                    </div>
                </div>

                <!-- Matching Criteria -->
                <div class="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">Matching Criteria</h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Select incident type and whether this applies to incidents, near misses, or both. This policy will automatically apply to all severities, priorities, and locations.</p>
                    </div>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="incident_type_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Incident Type
                            </label>
                            <select
                                id="incident_type_id"
                                v-model="form.incident_type_id"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option value="">All Types</option>
                                <option v-for="type in incidentTypes" :key="type.id" :value="type.id">
                                    {{ type.name }}
                                </option>
                            </select>
                        </div>

                        <div>
                            <label for="near_miss_only" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Incident/Near Miss Filter
                            </label>
                            <select
                                id="near_miss_only"
                                v-model="form.near_miss_only"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                <option :value="null">Both Incidents and Near Misses</option>
                                <option :value="false">Incidents Only</option>
                                <option :value="true">Near Misses Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- SLA Times -->
                <div class="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white">SLA Times</h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Define time targets in hours. Leave empty if not applicable.</p>
                    </div>

                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                            <label for="response_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Response Time (hours)
                            </label>
                            <input
                                id="response_time_hours"
                                v-model.number="form.response_time_hours"
                                type="number"
                                min="0"
                                step="0.5"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="e.g., 2"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Time to complete triage/initial response</p>
                        </div>

                        <div>
                            <label for="investigation_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Investigation Time (hours)
                            </label>
                            <input
                                id="investigation_time_hours"
                                v-model.number="form.investigation_time_hours"
                                type="number"
                                min="0"
                                step="0.5"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="e.g., 48"
                            />
                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Time to complete full investigation</p>
                        </div>
                    </div>
                </div>

                <!-- Status -->
                <div class="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Status</h3>

                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                            <p class="text-gray-500 dark:text-gray-400">Enable this SLA policy to apply to matching incidents</p>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Link
                        :href="route('settings.incidents.sla-policies.index')"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {{ form.processing ? 'Creating...' : 'Create SLA Policy' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    incidentTypes: {
        type: Array,
        required: true,
    },
});

// Initialize form with useSecureForm for company context protection
const form = useSecureForm({
    name: '',
    description: '',
    incident_type_id: '',
    near_miss_only: null,
    response_time_hours: null,
    investigation_time_hours: null,
    is_active: true,
});

const submit = () => {
    form.post(route('settings.incidents.sla-policies.store'), {
        preserveScroll: true,
    });
};
</script>
